import { NavigateNext } from '@mui/icons-material';
import { Breadcrumbs, Container, Divider, Typography } from '@mui/material';
import type { ComponentProps, ElementType, ReactNode } from 'react';

import { useBreakpoint } from '../../hooks/useBreakpoint/useBreakpoint';
import { BackButton, type BackButtonProps } from '../BackButton/BackButton';
import { StickyElement } from '../StickyElement/StickyElement';
import { TypographyIcon } from '../TypographyIcon/TypographyIcon';

export const Page = ({ children, fullWidth = true }: { children: ReactNode; fullWidth?: boolean }) => (
  <Container
    maxWidth={fullWidth ? false : 'lg'}
    sx={({ constants: { groundControlNavbarHeight }, palette }) => ({
      backgroundColor: palette.background.default,
      minHeight: `calc(100vh - ${groundControlNavbarHeight}px)`,
      p: {
        sm: 3,
        xs: 2,
      },
    })}
  >
    {children}
  </Container>
);

type TitleNonStickyProps = {
  sticky?: false | undefined;
  headerHeight?: never;
};

type TitleStickyProps = {
  sticky: true;
  headerHeight: number;
};

type TitleCommonProps = {
  title: string;
  subtitle?: string;
  icon?: ElementType;
  titleProps?: Omit<ComponentProps<typeof Typography<'h1'>>, 'children' | 'variant' | 'component'>;
  subtitleProps?: Omit<ComponentProps<typeof Typography>, 'children'>;
};

type TitleProps = TitleCommonProps & (TitleNonStickyProps | TitleStickyProps);

const PageTitle = ({ title, subtitle, icon, titleProps, subtitleProps, sticky, headerHeight }: TitleProps) => {
  const { isMobile } = useBreakpoint();
  const baseSubtitleProps = {
    color: '#0D0C1D99',
    component: 'span',
    display: 'block',
    mt: 2,
    title: subtitle,
    variant: 'body2',
    ...subtitleProps,
  } as const;

  if (sticky) {
    return (
      <StickyElement headerHeight={headerHeight}>
        <Typography variant={isMobile ? 'h5' : 'h2'} component="h1" {...titleProps}>
          {title}
          {subtitle ? (
            icon ? (
              <TypographyIcon {...baseSubtitleProps} icon={icon}>
                {subtitle}
              </TypographyIcon>
            ) : (
              <Typography {...baseSubtitleProps}>{subtitle}</Typography>
            )
          ) : null}
        </Typography>
      </StickyElement>
    );
  }
  return (
    <Typography variant={isMobile ? 'h5' : 'h2'} component="h1" {...titleProps}>
      {title}
      {subtitle ? (
        icon ? (
          <TypographyIcon {...baseSubtitleProps} icon={icon}>
            {subtitle}
          </TypographyIcon>
        ) : (
          <Typography {...baseSubtitleProps}>{subtitle}</Typography>
        )
      ) : null}
    </Typography>
  );
};

const PageDivider = () => <Divider sx={{ my: 2 }} />;

const PageBackButton = <C extends React.ElementType = 'button'>({
  children,
  ...rest
}: Omit<BackButtonProps<C>, 'sx'>) => {
  return (
    // @ts-expect-error - `sx` prop is not recognized by `BackButton`
    <BackButton<C>
      sx={{
        mb: 1.5,
      }}
      {...rest}
    >
      {children}
    </BackButton>
  );
};

const PageBreadcrumbs = ({ children }: { children: ReactNode }) => (
  <Breadcrumbs
    sx={{
      mb: 1.5,
    }}
    separator={<NavigateNext fontSize="small" />}
    aria-label="breadcrumb"
  >
    {children}
  </Breadcrumbs>
);

Page.Title = PageTitle;

Page.Divider = PageDivider;

Page.BackButton = PageBackButton;

Page.Breadcrumbs = PageBreadcrumbs;
